import React, { useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import emailjs from 'emailjs-com';
import SectionHeading from './shared/SectionHeading';
import PrimaryButton from './shared/PrimaryButton';
import SecondaryButton from './shared/SecondaryButton';
import serviceAreas from '../data/serviceAreas';
import {
  EMAIL_ADDRESS,
  EMAIL_LINK,
  EMERGENCY_HOURS,
  PHONE_NUMBER,
  PHONE_NUMBER_LINK,
  REGULAR_HOURS,
  SECONDARY_CTA_TEXT,
  SERVICE_AREA_SUMMARY,
  SMS_LINK,
} from '../data/contact';
import heroImage from '../assets/contact-hero-optimized.jpg';

const reassuranceItems = [
  'Fast response for urgent heating and cooling issues',
  'Local service centered on South Jordan and nearby communities',
  'Clear communication about the next step before work begins',
];

const quickFacts = [
  {
    icon: <PhoneOutlinedIcon fontSize="small" />,
    title: 'Call Preferred Service',
    body: PHONE_NUMBER,
    href: PHONE_NUMBER_LINK,
  },
  {
    icon: <EmailOutlinedIcon fontSize="small" />,
    title: 'Email Us',
    body: EMAIL_ADDRESS,
    href: EMAIL_LINK,
  },
  {
    icon: <ScheduleOutlinedIcon fontSize="small" />,
    title: 'Hours',
    body: `${REGULAR_HOURS} | ${EMERGENCY_HOURS}`,
  },
  {
    icon: <PlaceOutlinedIcon fontSize="small" />,
    title: 'Service Area',
    body: SERVICE_AREA_SUMMARY,
  },
];

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  message: '',
  contactMethod: 'email',
};

const ContactUs = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!event.currentTarget.checkValidity()) {
      event.currentTarget.reportValidity();
      return;
    }

    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userID = process.env.REACT_APP_EMAILJS_USER_ID;

    if (!serviceID || !templateID || !userID) {
      setStatus({
        open: true,
        severity: 'error',
        message: `Online messaging is temporarily unavailable. Please call ${PHONE_NUMBER}.`,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await emailjs.send(serviceID, templateID, form, userID);
      setForm(INITIAL_FORM);
      setStatus({
        open: true,
        severity: 'success',
        message: 'Thank you. Your message has been sent.',
      });
    } catch (error) {
      setStatus({
        open: true,
        severity: 'error',
        message: `We could not send your message. Please try again or call ${PHONE_NUMBER}.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => setStatus((current) => ({ ...current, open: false }));

  return (
    <main>
      <section className="contact-page-hero section">
        <div className="container contact-page-hero-grid">
          <div className="contact-page-hero-copy">
            <p className="section-eyebrow">Contact Preferred Service</p>
            <h1>Reach out for straightforward HVAC help and a clear next step.</h1>
            <p className="contact-page-hero-text">
              Whether you need a repair, maintenance visit, or replacement guidance, use the form
              below or call directly. We keep communication simple and responsive.
            </p>

            <div className="hero-actions">
              <PrimaryButton href="#contact-form">Start Your Message</PrimaryButton>
              <SecondaryButton href={PHONE_NUMBER_LINK} aria-label={`Call Now at ${PHONE_NUMBER}`}>
                {SECONDARY_CTA_TEXT}
              </SecondaryButton>
            </div>

            <a
              href={SMS_LINK}
              className="button button-secondary contact-mobile-text-cta"
              aria-label={`Text ${PHONE_NUMBER} for fast service`}
            >
              Text for Fast Service
            </a>
            <p className="contact-mobile-text-copy">
              Prefer texting? Send us a message and we will get back to you quickly.
            </p>

            <ul className="conversion-cta-microcopy" aria-label="Why homeowners contact Preferred Service">
              {reassuranceItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="contact-page-hero-media-wrap">
            <div className="contact-page-hero-media">
              <img
                src={heroImage}
                alt="Preferred Service HVAC equipment and installation work"
                decoding="async"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container contact-page-layout">
          <div className="card contact-form-card">
            <SectionHeading
              eyebrow="Send A Message"
              title="Tell us what is going on and how you would like us to follow up."
              description="Keep it simple. Share the issue, choose your preferred contact method, and we will respond as soon as we can."
            />

            <form
              id="contact-form"
              className="contact-form"
              aria-label="Service request form"
              onSubmit={handleSubmit}
            >
              <div className="contact-form-grid">
                <label className="contact-field">
                  <span>Name</span>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                  />
                </label>

                <label className="contact-field">
                  <span>Preferred Contact Method</span>
                  <select
                    id="contactMethod"
                    name="contactMethod"
                    value={form.contactMethod}
                    onChange={handleChange}
                    required
                  >
                    <option value="email">Email</option>
                    <option value="call">Call</option>
                    <option value="text">Text</option>
                  </select>
                </label>
              </div>

              {form.contactMethod === 'email' ? (
                <label className="contact-field">
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    required
                  />
                </label>
              ) : (
                <label className="contact-field">
                  <span>Phone Number</span>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(801) 555-1234"
                    required
                  />
                </label>
              )}

              <label className="contact-field">
                <span>How can we help?</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about the repair, maintenance, or comfort issue you are dealing with."
                  rows="6"
                  required
                />
              </label>

              <button
                type="submit"
                className="button button-primary contact-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending…' : 'Request A Response'}
              </button>
            </form>
          </div>

          <div className="contact-side-column">
            <div className="card contact-info-card">
              <SectionHeading
                eyebrow="Contact Details"
                title="Everything you need to reach the team quickly."
                description="If you are nearby and not sure whether your city is covered, reach out anyway. We can confirm the next step fast."
              />
              <div className="contact-info-stack">
                {quickFacts.map((item) => (
                  <article key={item.title} className="contact-info-item">
                    <span className="contact-quick-icon" aria-hidden="true">
                      {item.icon}
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      {item.href ? (
                        <a href={item.href} className="contact-quick-link">
                          {item.body}
                        </a>
                      ) : (
                        <p>{item.body}</p>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="card contact-service-area-card">
              <p className="section-eyebrow">Nearby Communities</p>
              <div className="service-area-list">
                {serviceAreas.map((area) => (
                  <span key={area} className="service-area-pill">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Snackbar
        open={status.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={status.severity} sx={{ width: '100%' }}>
          {status.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default ContactUs;
